import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import { Avatar, Badge, Loading, useLoad, eur, num, btnGhost } from '@/components/dashboard/kit';
import { TierBadge, plural } from './people';

/*
  Red completa del club (decisión de Samuel, 10/10/2026): todos y todos los niveles, para admin y superadmin.
  Plataforma (patrocinio) y CDM unidos por email en el servidor (GET /admin/network, routes/admin_people.py).
  Raíces arriba (el equipo primero); cada nodo con nivel, directos y total por debajo; se despliega por ramas
  (solo se pinta lo abierto: aguanta cientos de personas). El buscador encuentra a alguien y abre su rama.
  Al pulsar un nombre se abre su ficha. Comisiones por nodo solo para el superadmin (el servidor no las manda al resto).
*/
const ROOTS_STEP = 50;
const fold = (v) => String(v || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export default function NetworkTree({ query = '', onOpen }) {
  const { data, error } = useLoad(() => dashboardApi.adminNetwork(), []);
  const [open, setOpen] = useState(() => new Set());
  const [shownRoots, setShownRoots] = useState(ROOTS_STEP);
  const [focus, setFocus] = useState(null);

  const { byKey, children } = useMemo(() => {
    const bk = {}; const ch = {};
    (data?.nodes || []).forEach((n) => { bk[n.key] = n; if (n.parent) (ch[n.parent] = ch[n.parent] || []).push(n.key); });
    Object.values(ch).forEach((list) => list.sort((a, b) => bk[b].total - bk[a].total || fold(bk[a].name).localeCompare(fold(bk[b].name))));
    return { byKey: bk, children: ch };
  }, [data]);

  const q = fold(query.trim());
  const matches = useMemo(() => (q.length < 2 ? [] : (data?.nodes || [])
    .filter((n) => fold(n.name).includes(q) || fold(n.email).includes(q)).slice(0, 8)), [q, data]);

  // Abre la rama hasta una persona, la marca y la lleva a la vista
  const reveal = (key) => {
    const next = new Set(open);
    let cur = byKey[key]?.parent; let root = key;
    while (cur) { next.add(cur); root = cur; cur = byKey[cur]?.parent; }
    setOpen(next);
    const idx = data.roots.indexOf(root);
    if (idx >= shownRoots) setShownRoots(idx + 1);
    setFocus(key);
  };
  useEffect(() => {
    if (!focus) return;
    const t = setTimeout(() => document.querySelector(`[data-node="${CSS.escape(focus)}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 60);
    return () => clearTimeout(t);
  }, [focus, open]);

  if (!data) return <Loading error={error} />;
  const toggle = (key) => setOpen((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const expandAll = () => setOpen(new Set(Object.keys(children)));
  const roots = data.roots.slice(0, shownRoots);

  return (
    <div className="space-y-3" data-testid="network-tree">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-400">
          {plural(data.total, 'persona', 'personas')} · {plural(data.max_depth, 'nivel', 'niveles')} · {plural(data.roots.length, 'raíz', 'raíces')}
          {data.cdm ? ' · plataforma y CDM unidos por email' : ''}
        </p>
        <div className="flex gap-2">
          <button type="button" onClick={expandAll} className={`${btnGhost} h-10`}>Expandir todo</button>
          <button type="button" onClick={() => { setOpen(new Set()); setFocus(null); }} className={`${btnGhost} h-10`}>Contraer todo</button>
        </div>
      </div>

      {q.length >= 2 && (
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-2" data-testid="network-matches">
          {!matches.length ? <p className="px-2 py-2 text-sm text-gray-400">Nadie en la red coincide con «{query.trim()}».</p> : (
            <ul className="space-y-1">
              {matches.map((n) => (
                <li key={n.key}>
                  <button type="button" onClick={() => reveal(n.key)} className="w-full min-h-[44px] flex items-center gap-3 rounded-lg px-2 text-left hover:bg-white/[0.04]">
                    <Avatar name={n.name} size="sm" />
                    <span className="min-w-0 flex-1"><span className="block text-sm text-white truncate">{n.name}</span>
                      <span className="block text-xs text-gray-400 truncate">{n.parent ? `Bajo ${byKey[n.parent]?.name}` : 'Raíz'} · nivel {n.depth + 1}</span></span>
                    <span className="text-xs text-gold whitespace-nowrap">Ver en la red</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="rounded-xl border border-white/[0.06] bg-white/[0.01] p-2 sm:p-3 overflow-x-auto">
        {!roots.length ? <p className="py-8 text-center text-sm text-gray-400">Todavía no hay nadie en la red.</p> : roots.map((k) => (
          <Branch key={k} k={k} byKey={byKey} kidsOf={children} open={open} toggle={toggle} focus={focus} onOpen={onOpen} money={data.money} />
        ))}
        {data.roots.length > shownRoots && (
          <button type="button" onClick={() => setShownRoots((n) => n + ROOTS_STEP)} className={`${btnGhost} h-10 mt-2`}>
            Mostrar más raíces ({data.roots.length - shownRoots})
          </button>
        )}
      </div>
    </div>
  );
}

function Branch({ k, byKey, kidsOf, open, toggle, focus, onOpen, money }) {
  const n = byKey[k];
  const kids = kidsOf[k] || [];
  const isOpen = open.has(k);
  return (
    <div>
      <div data-node={k} className={`flex items-center gap-2 min-w-[260px] rounded-lg pr-2 ${focus === k ? 'bg-gold/10 ring-1 ring-gold/40' : 'hover:bg-white/[0.02]'}`}>
        {kids.length ? (
          <button type="button" onClick={() => toggle(k)} aria-expanded={isOpen} aria-label={`${isOpen ? 'Contraer' : 'Desplegar'} la red de ${n.name}`}
            className="w-10 h-10 shrink-0 inline-flex items-center justify-center rounded-lg text-gray-400 hover:text-gold">
            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        ) : <span className="w-10 h-10 shrink-0" aria-hidden="true" />}
        <Avatar name={n.name} size="sm" />
        <span className="min-w-0 flex-1 py-1.5">
          {n.openable ? (
            <button type="button" onClick={() => onOpen(n.key)} className="block max-w-full truncate text-left text-sm text-white hover:text-gold">{n.name}</button>
          ) : <span className="block truncate text-sm text-white" title="Solo está en el CDM: sin ficha en la plataforma">{n.name}</span>}
          <span className="block text-xs text-gray-400 truncate">
            {kids.length ? `${plural(n.directs, 'directo', 'directos')} · ${num(n.total)} en total` : 'Sin red por debajo'}
            {money && n.commission !== undefined && <span className="md:hidden"> · {eur(n.commission)}</span>}
          </span>
        </span>
        <span className="hidden sm:flex items-center gap-1.5 shrink-0">
          {n.is_broker && <Badge tone="gold">Broker</Badge>}
          {n.source === 'cdm' && <Badge title={`Ficha del CDM ${n.cdm_ids.join(', ')}`}>Solo CDM</Badge>}
        </span>
        {n.source === 'cdm' && n.tier === 'sin_cuenta'
          ? <span className="sm:hidden"><Badge>Solo CDM</Badge></span>
          : <TierBadge tier={n.tier} />}
        {money && n.commission !== undefined && <span className="hidden md:inline text-sm text-white tabular-nums w-28 text-right">{eur(n.commission)}</span>}
      </div>
      {isOpen && kids.length > 0 && (
        <div className="ml-4 sm:ml-5 pl-1 sm:pl-2 border-l border-white/[0.06]">
          {kids.map((c) => <Branch key={c} k={c} byKey={byKey} kidsOf={kidsOf} open={open} toggle={toggle} focus={focus} onOpen={onOpen} money={money} />)}
        </div>
      )}
    </div>
  );
}
